import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import DOMPurify from 'dompurify'
import { Eye, EyeOff } from 'lucide-react'

import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtils/TextInput'
import { EditorRHF } from 'components/Editor/EditorRHF'
import { handleFormError } from 'utils/handleFormError'

import {
  type SenderEmail,
  type UpdateSenderEmailPayload,
  useUpdateSenderEmail,
} from 'features/campaigns/sender_emails_api'

// Must match server/emailbison/constants.py MAX_DAILY_LIMIT_PER_DOMAIN.
const MAX_DAILY_LIMIT = 20

// Stored signatures are plain text. Wrap them in HTML for Quill so that
// newline-separated lines render as separate paragraphs instead of
// collapsing onto one line (HTML treats raw `\n` as whitespace).
function plainTextToEditorHtml(text: string): string {
  if (!text) return ''
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .split('\n')
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('')
}

interface EditSenderEmailModalProps {
  isOpen: boolean
  onClose: () => void
  senderEmail: SenderEmail | null
}

interface EditSenderEmailForm {
  name: string
  daily_limit: number
  email_signature: string
}

export function EditSenderEmailModal({
  isOpen,
  onClose,
  senderEmail,
}: EditSenderEmailModalProps) {
  const updateMutation = useUpdateSenderEmail()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors },
    watch,
    setError,
  } = useForm<EditSenderEmailForm>({
    defaultValues: {
      name: senderEmail?.name ?? '',
      daily_limit: Math.min(senderEmail?.daily_limit ?? 0, MAX_DAILY_LIMIT),
      email_signature: plainTextToEditorHtml(
        senderEmail?.email_signature ?? ''
      ),
    },
    mode: 'onChange',
  })

  const onSubmit = handleSubmit(async (values) => {
    if (!senderEmail) return

    // Always send all fields — EB API requires daily_limit on every PATCH
    const payload: UpdateSenderEmailPayload = {
      name: values.name,
      daily_limit: values.daily_limit,
      email_signature: values.email_signature,
    }

    const [err, result] = await to(
      updateMutation.mutateAsync({
        senderEmailId: senderEmail.id,
        payload,
      })
    )

    if (err) {
      handleFormError(err, setError)
      return
    }

    toast.success('Sender email updated successfully')
    result?.signature_warnings?.forEach((w) => toast.warning(w))
    onClose()
  })

  return (
    <Modal
      open={isOpen}
      title="Edit Sender Email"
      description={
        senderEmail ? `Editing settings for ${senderEmail.email}` : ''
      }
      onOpenChange={(open) => !open && onClose()}
      loading={isSubmitting}
      blockAccept={!isDirty}
      onAccept={onSubmit}
      acceptButtonText="Save Changes"
      size="md"
    >
      <div className="space-y-4">
        <TextInput
          name="name"
          control={control}
          label="Display Name"
          placeholder="Enter display name"
          rules={{ required: 'Name is required' }}
        />

        <TextInput
          name="daily_limit"
          control={control}
          label="Daily Limit"
          placeholder={`Enter daily send limit (max ${MAX_DAILY_LIMIT})`}
          type="number"
          rules={{
            required: 'Daily limit is required',
            min: { value: 1, message: 'Minimum value is 1' },
            max: {
              value: MAX_DAILY_LIMIT,
              message: `Maximum is ${MAX_DAILY_LIMIT} emails per day`,
            },
          }}
        />

        <div className="[&_.ql-editor]:min-h-[120px]">
          <EditorRHF
            control={control}
            name="email_signature"
            label="Email Signature"
            optional
            placeholder="Enter email signature..."
            plainText
          />
          {errors.email_signature?.message && (
            <p className="mt-1 text-xs text-error">
              {errors.email_signature.message as string}
            </p>
          )}
        </div>

        <SignaturePreview signatureHtml={watch('email_signature')} />
      </div>
    </Modal>
  )
}

function SignaturePreview({ signatureHtml }: { signatureHtml: string }) {
  const [showPreview, setShowPreview] = useState(false)

  const isEmpty =
    !signatureHtml ||
    signatureHtml === '<p><br></p>' ||
    signatureHtml === '<p></p>'

  if (isEmpty) return null

  return (
    <div>
      <button
        type="button"
        onClick={() => setShowPreview((prev) => !prev)}
        className="flex items-center gap-1.5 text-xs text-brand hover:underline mb-2"
      >
        {showPreview ? (
          <EyeOff className="size-3" />
        ) : (
          <Eye className="size-3" />
        )}
        {showPreview ? 'Hide preview' : 'Show preview'}
      </button>
      {showPreview && (
        <div
          className="border border-line rounded-lg p-3 bg-surface-secondary text-sm"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(signatureHtml),
          }}
        />
      )}
    </div>
  )
}
